import React, { useState, useEffect, useRef } from "react";
import { ChevronUp, ChevronDown, X, Search } from "lucide-react";

const MultiSelectDropdown = ({
    options = [],
    value = "",
    onChange,
    placeholder = "Select items",
    searchPlaceholder = "Search...",
    isWhiteLabelingEnable = true,
    required = false,
    maxSelection = null,
    error = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);

    // Parse the current value (comma-separated string) into an array
    const selectedItems = value
        ? value.split(",").map((l) => l.trim()).filter(Boolean)
        : [];

    // Check if all individual options are selected, if so, add "All" to selectedItems
    const allOptions = options.filter(o => o !== "All");
    if (allOptions.every(opt => selectedItems.includes(opt))) {
        selectedItems.push("All");
    }

    const toggleDropdown = () => {
        setIsOpen(!isOpen);
        if (!isOpen) {
            setTimeout(() => inputRef.current?.focus(), 0);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm("");
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filteredOptions = options
        .filter((option) =>
            option.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .sort((a, b) => {
            const aSelected = selectedItems.includes(a);
            const bSelected = selectedItems.includes(b);

            if (aSelected && !bSelected) return -1;
            if (!aSelected && bSelected) return 1;
            return a.localeCompare(b);
        });

    const handleSelect = (option) => {
        let newValue;
        if (option === "All") {
            if (selectedItems.includes("All")) {
                // Deselect all
                newValue = "";
            } else {
                // Select all
                const allOptions = options.filter(o => o !== "All");
                newValue = ["All", ...allOptions].join(", ");
            }
        } else {
            if (selectedItems.includes(option)) {
                // Remove if already selected
                newValue = selectedItems.filter((item) => item !== option).join(", ");
            } else {
                // Check maxSelection
                if (maxSelection && selectedItems.length >= maxSelection) {
                    return; // Do nothing if limit reached
                }
                // Add if not selected
                newValue = [...selectedItems, option].join(", ");
            }
        }
        onChange(newValue);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Escape") {
            setIsOpen(false);
            setSearchTerm("");
        }
    };

    return (
        <div className="relative w-full" ref={dropdownRef}>
            <div
                className={`w-full px-4 py-3 bg-transparent border ${error ? "border-red-500 dark:border-red-500" : (isOpen ? "border-[#C1A0D0] dark:border-[#C1A0D0]" : "border-gray-400 dark:border-[rgba(229,229,229,0.5)]")} rounded-lg transition-colors flex justify-between items-center cursor-pointer`}
                onClick={toggleDropdown}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleDropdown();
                    }
                }}
                tabIndex={0}
                role="combobox"
                aria-expanded={isOpen}
            >
                <div className="flex flex-wrap gap-1 items-center overflow-hidden">
                    {selectedItems.length > 0 ? (
                        <span
                            className={`truncate ${isWhiteLabelingEnable ? "text-gray-700 dark:text-gray-200" : "text-gray-300"
                                }`}
                        >
                            {selectedItems.filter(item => item !== "All").join(", ")}
                        </span>
                    ) : (
                        <span className="text-gray-400 dark:text-gray-500 text-sm whitespace-nowrap">
                            {placeholder}
                        </span>
                    )}
                </div>
                <div className="flex items-center ml-2">
                    {isOpen ? (
                        <ChevronUp className="w-5 h-5 text-gray-400 dark:text-gray-400" />
                    ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400 dark:text-gray-400" />
                    )}
                </div>
            </div>

            {isOpen && (
                <div
                    className="absolute z-[100] w-full mt-1 bg-slate-50 dark:bg-[#0a1733] border border-gray-400 dark:border-[rgba(229,229,229,0.5)] rounded-lg shadow-xl overflow-hidden"
                >
                    <div className="p-3 border-b border-gray-200 dark:border-white/10">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-400" />
                            <input
                                ref={inputRef}
                                type="text"
                                className="w-full pl-10 pr-4 py-2 bg-white dark:bg-white/5 border border-gray-300 dark:border-white/10 rounded-md text-gray-700 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-500 focus:outline-none focus:border-[#C1A0D0] dark:focus:border-[#C1A0D0] text-sm"
                                placeholder={searchPlaceholder}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                        </div>
                    </div>
                    <ul className="max-h-60 overflow-y-auto py-1">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option, index) => (
                                <li
                                    key={index}
                                    className={`px-4 py-2 flex items-center justify-between cursor-pointer transition-colors ${selectedItems.includes(option)
                                        ? "bg-[#C1A0D0]/40 dark:bg-[#C1A0D0]/20 text-gray-800 dark:text-gray-100"
                                        : "text-gray-600 dark:text-gray-300 hover:bg-[#C1A0D0]/10 dark:hover:bg-white/5"
                                        }`}
                                    onClick={() => handleSelect(option)}
                                >
                                    <span className="text-sm">{option}</span>
                                    {selectedItems.includes(option) && (
                                        <X className="w-4 h-4 text-[#C1A0D0]" />
                                    )}
                                </li>
                            ))
                        ) : (
                            <li className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-center">
                                No items found
                            </li>
                        )}
                    </ul>
                </div>
            )}

            {required && selectedItems.length === 0 && (
                <input
                    tabIndex={-1}
                    autoComplete="off"
                    style={{
                        opacity: 0,
                        height: 0,
                        padding: 0,
                        margin: 0,
                        position: "absolute",
                    }}
                    required={required}
                />
            )}
        </div>
    );
};

export default MultiSelectDropdown;
